import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildBoothSharePath,
  buildSharePath,
  type BoothManifest,
  type CaptureMode,
  type EffectIdName,
} from '@photobooth/shared';
import type { CreateUploadRequest } from '@photobooth/shared';

import styles from '../styles/app.module.css';
import { useCamera } from '../camera/useCamera';
import { useFaceTracker } from '../face/useFaceTracker';
import { createGpuEngine, hasWebGl2, hasWebGpu } from '../gpu/engine';
import type { GpuEngine } from '../gpu/engine';
import { getEffect } from '../gpu/registry';
import { useBoothStore } from '../state/store';
import { composeStrip } from '../capture/strip';
import { compositeFrameOnto } from '../capture/composite';
import { recordVideo } from '../capture/video';
import { runCountdown } from '../capture/countdown';
import { negotiateVideoMime } from '../utils/mimeNegotiate';
import { createUpload, uploadBlob } from '../api/client';
import { EffectDrawer } from '../ui/EffectDrawer';
import { ModePicker } from '../ui/ModePicker';
import { CountdownOverlay } from '../ui/CountdownOverlay';
import { ShutterButton } from '../ui/ShutterButton';
import { Flash } from '../ui/Flash';
import { CameraFlipButton } from '../ui/CameraFlipButton';
import { Toast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { FooterCredit } from '../ui/FooterCredit';
import { MakeYourOwnButton } from '../ui/MakeYourOwnButton';
import { isIosSafari, isMobile } from '../utils/isMobile';
import { logger } from '../utils/log';
import { useEasterEgg } from '../utils/easterEgg';
import { pickFrameForCanvas } from '../builder/frameAspect';
import { createBoothCapture } from '../api/client';
import { UnsupportedPage } from './UnsupportedPage';

const log = logger('booth');

const VIDEO_MAX_SECONDS = 30;
const SHOTS_BY_MODE = { single: 1, strip3: 3, strip4: 4, video: 0 } as const;

export interface BoothPageProps {
  /** When set, the booth runs in "custom" mode with these limits/assets. */
  manifest?: BoothManifest | null;
}

export function BoothPage({ manifest = null }: BoothPageProps = {}) {
  const navigate = useNavigate();
  const camera = useCamera({ audio: true, facingMode: 'user' });
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<GpuEngine | null>(null);
  const [engineBackend, setEngineBackend] = useState<'webgpu' | 'webgl2' | null>(null);
  const [engineUnsupported, setEngineUnsupported] = useState(false);
  const [videoElReady, setVideoElReady] = useState(false);
  const [flash, setFlash] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; kind: 'photo' | 'video'; blob: Blob } | null>(null);
  // Badge stats (fps, camera fps, dims) are written imperatively to the DOM
  // so they don't trigger React re-renders. Cached vars track the current
  // displayed values so we only mutate textContent when something changes.
  const badgeStatsRef = useRef<HTMLSpanElement | null>(null);
  const badgeStatsCache = useRef({ fps: -1, cam: -1, vw: 0, vh: 0, cw: 0, ch: 0 });
  const writeBadgeStats = useCallback(() => {
    const el = badgeStatsRef.current;
    if (!el) return;
    const c = badgeStatsCache.current;
    let text = ` · ${c.fps} fps`;
    if (c.cam > 0) text += ` · cam ${c.cam}fps`;
    if (c.vw > 0) text += ` · v${c.vw}x${c.vh} c${c.cw}x${c.ch}`;
    el.textContent = text;
  }, []);
  const [rotationOverride, setRotationOverride] = useState<0 | 1 | 2 | 3 | null>(
    () => {
      if (typeof localStorage === 'undefined') return null;
      const v = localStorage.getItem('photobooth:rotation-override');
      if (v === '0' || v === '1' || v === '2' || v === '3') {
        return Number(v) as 0 | 1 | 2 | 3;
      }
      return null;
    },
  );

  // Stats badge is hidden by default; surfaced by typing "stat" on
  // desktop or shaking the device on mobile.
  const [statsVisible, setStatsVisible] = useState(false);
  useEasterEgg(useCallback(() => setStatsVisible((v) => !v), []));

  const cycleRotation = useCallback(() => {
    setRotationOverride((prev) => {
      const next: 0 | 1 | 2 | 3 | null =
        prev === null ? 0 : prev === 3 ? null : ((prev + 1) as 0 | 1 | 2 | 3);
      if (typeof localStorage !== 'undefined') {
        if (next === null) localStorage.removeItem('photobooth:rotation-override');
        else localStorage.setItem('photobooth:rotation-override', String(next));
      }
      return next;
    });
  }, []);

  // Push rotation override down to the engine whenever it changes.
  useEffect(() => {
    engineRef.current?.setRotationOverride(rotationOverride);
  }, [rotationOverride, engineBackend]);

  // Imperative FPS poll → no React re-render.
  useEffect(() => {
    if (!engineBackend) return;
    const id = window.setInterval(() => {
      const e = engineRef.current;
      if (!e) return;
      const fps = e.getLastFps();
      if (badgeStatsCache.current.fps !== fps) {
        badgeStatsCache.current.fps = fps;
        writeBadgeStats();
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [engineBackend, writeBadgeStats]);

  // Imperative camera fps poll.
  useEffect(() => {
    if (!camera.stream) return;
    const update = () => {
      const track = camera.stream?.getVideoTracks()[0];
      const s = track?.getSettings();
      const cam = typeof s?.frameRate === 'number' ? Math.round(s.frameRate) : -1;
      if (badgeStatsCache.current.cam !== cam) {
        badgeStatsCache.current.cam = cam;
        writeBadgeStats();
      }
    };
    update();
    const id = window.setInterval(update, 2000);
    return () => window.clearInterval(id);
  }, [camera.stream, writeBadgeStats]);

  // Granular selectors — each subscribes only to its own slice so unrelated
  // store changes don't force a BoothPage re-render. The setters are stable
  // function refs from Zustand so we pull them from the static getState().
  const mode = useBoothStore((s) => s.mode);
  const effect = useBoothStore((s) => s.effect);
  const phase = useBoothStore((s) => s.phase);
  const countdownValue = useBoothStore((s) => s.countdownValue);
  const recordingProgress = useBoothStore((s) => s.recordingProgress);
  const errorMessage = useBoothStore((s) => s.errorMessage);
  const faceTrackingAvailable = useBoothStore((s) => s.faceTrackingAvailable);
  const {
    setMode,
    setEffect,
    setPhase,
    setCountdown,
    setRecordingProgress,
    setError,
    setFaceTrackingAvailable,
    pushShare,
  } = useBoothStore.getState();

  // GPU engine boot.
  useEffect(() => {
    let cancelled = false;
    if (!hasWebGpu() && !hasWebGl2()) {
      setEngineUnsupported(true);
      return;
    }
    // Allow forcing a backend via ?backend=webgpu|webgl2 for A/B perf testing.
    const params =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : null;
    const forced = params?.get('backend');
    const prefer: 'webgpu' | 'webgl2' | undefined =
      forced === 'webgl2' || forced === 'webgpu' ? forced : undefined;
    (async () => {
      try {
        log.info('mount: creating engine', { prefer });
        const engine = await createGpuEngine({ prefer });
        log.info('engine created', engine.backend);
        if (cancelled) {
          engine.destroy();
          return;
        }
        const canvas = canvasRef.current;
        if (!canvas) {
          log.warn('canvas ref not ready at engine init');
          return;
        }
        await engine.init(canvas);
        log.info('engine.init complete');
        if (cancelled) {
          engine.destroy();
          return;
        }
        engineRef.current = engine;
        setEngineBackend(engine.backend);
        engine.setMirror(camera.facingMode === 'user');
        engine.setRotationOverride(rotationOverride);
        const v = videoElRef.current;
        if (v) engine.setSource(v);
        engine.setEffect('normal');
        engine.start();
        log.info('engine.start (render loop running)');
      } catch (err) {
        log.error('engine init failed', err);
        setEngineUnsupported(true);
      }
    })();
    return () => {
      cancelled = true;
      engineRef.current?.destroy();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize the offscreen canvas to a sane device-pixel ratio. We listen on
  // ResizeObserver AND window resize / orientationchange because Safari on
  // iOS sometimes settles the layout late after rotation.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const MAX_LONG_SIDE = 1440;
      let w = Math.max(1, Math.floor(rect.width * dpr));
      let h = Math.max(1, Math.floor(rect.height * dpr));
      const longest = Math.max(w, h);
      if (longest > MAX_LONG_SIDE) {
        const k = MAX_LONG_SIDE / longest;
        w = Math.max(1, Math.floor(w * k));
        h = Math.max(1, Math.floor(h * k));
      }
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      const v = videoElRef.current;
      const c = badgeStatsCache.current;
      const vw = v?.videoWidth ?? 0;
      const vh = v?.videoHeight ?? 0;
      if (c.vw !== vw || c.vh !== vh || c.cw !== w || c.ch !== h) {
        c.vw = vw;
        c.vh = vh;
        c.cw = w;
        c.ch = h;
        writeBadgeStats();
      }
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(canvas);
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    const v = videoElRef.current;
    v?.addEventListener('loadedmetadata', resize);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
      v?.removeEventListener('loadedmetadata', resize);
    };
  }, [engineBackend, videoElReady, camera.facingMode, writeBadgeStats]);

  // Hook the hidden <video> into the engine once it mounts.
  const setVideoEl = useCallback(
    (el: HTMLVideoElement | null) => {
      videoElRef.current = el;
      if (el) {
        camera.attachVideo(el);
        engineRef.current?.setSource(el);
        setVideoElReady(true);
      } else {
        setVideoElReady(false);
      }
    },
    [camera],
  );

  // Face tracking — lazy-loaded. We only initialize MediaPipe once the user
  // first picks an effect that benefits from face tracking. Until then, the
  // ~15MB of wasm + model file never gets fetched and the inference engine
  // stays asleep. Once enabled, it sticks for the rest of the session.
  const [faceTrackingNeeded, setFaceTrackingNeeded] = useState(false);
  useEffect(() => {
    if (faceTrackingNeeded) return;
    const def = getEffect(effect);
    if (def.category === 'distortion' || def.category === 'overlay') {
      log.info('face tracking demanded by effect', effect);
      setFaceTrackingNeeded(true);
    }
  }, [effect, faceTrackingNeeded]);

  const tracker = useFaceTracker({
    video: videoElRef.current,
    enabled:
      faceTrackingNeeded &&
      videoElReady &&
      camera.status.kind === 'ready',
  });

  useEffect(() => {
    // Only flip to "false" when we ACTUALLY tried and failed. Otherwise the
    // badge stays in its "unknown / not yet loaded" state (null), which the
    // UI renders as no text rather than the misleading "No face".
    if (tracker.state === 'ready') setFaceTrackingAvailable(true);
    else if (tracker.state === 'unavailable' && faceTrackingNeeded) {
      setFaceTrackingAvailable(false);
    } else {
      setFaceTrackingAvailable(null);
    }
  }, [tracker.state, faceTrackingNeeded, setFaceTrackingAvailable]);

  // Hand the engine a getter so it can poll the landmark ref each render
  // frame. This avoids a 15Hz React re-render storm from MediaPipe updates.
  // We depend on `engineBackend` so the wiring runs AFTER the async engine
  // init has completed (engineRef.current is null on the first mount tick).
  useEffect(() => {
    engineRef.current?.setFaceLandmarksSource(tracker.getLatest);
  }, [engineBackend, tracker.getLatest]);

  // Switch effects.
  useEffect(() => {
    engineRef.current?.setEffect(effect);
  }, [effect]);

  useEffect(() => {
    engineRef.current?.setMirror(camera.facingMode === 'user');
  }, [camera.facingMode]);

  // Disable face-dependent effects if face tracking is unavailable.
  useEffect(() => {
    if (faceTrackingAvailable === false) {
      const def = getEffect(effect);
      if (def.requiresFace) {
        setEffect('normal');
        setToast('Face tracking unavailable — disabled face effects');
      }
    }
  }, [faceTrackingAvailable, effect, setEffect]);

  // Toast auto-dismiss.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const runFlash = useCallback(async () => {
    setFlash(true);
    await new Promise((r) => setTimeout(r, 90));
    setFlash(false);
    await new Promise((r) => setTimeout(r, 140));
  }, []);

  // Holds the currently-active viewfinder frame URL so `grabFrame` can read
  // it without a forward reference to the `useMemo` further down the file.
  const activeFrameUrlRef = useRef<string | null>(null);

  const grabFrame = useCallback(async (): Promise<Blob> => {
    const engine = engineRef.current;
    if (!engine) throw new Error('Engine not ready');
    let blob = await engine.captureNext();
    const url = activeFrameUrlRef.current;
    if (url) {
      try {
        blob = await compositeFrameOnto(blob, url);
      } catch (err) {
        log.warn('frame composite failed; falling back to bare capture', err);
      }
    }
    return blob;
  }, []);

  const handleUpload = useCallback(
    async (blob: Blob, kind: 'photo' | 'video', contentType: string, ext: 'jpg' | 'mp4' | 'webm') => {
      setPhase('uploading');
      const req: CreateUploadRequest = { kind, contentType, ext };
      try {
        const ticket = manifest
          ? await createBoothCapture(manifest.slug, req)
          : await createUpload(req);
        await uploadBlob(ticket.uploadUrl, blob, contentType);
        pushShare({
          shareId: ticket.shareId,
          kind,
          shareUrl: ticket.shareUrl,
          mediaUrl: ticket.mediaUrl,
          capturedAt: Date.now(),
        });
        setPhase('done');
        const path = manifest
          ? buildBoothSharePath(manifest.slug, kind, ticket.shareId)
          : buildSharePath(kind, ticket.shareId);
        navigate(path, {
          state: { mediaUrl: ticket.mediaUrl, shareUrl: ticket.shareUrl, kind },
        });
      } catch (err) {
        log.error('upload failed', err);
        setError(err instanceof Error ? err.message : String(err));
        setPhase('error');
        setToast('Upload failed — try Download below.');
      }
    },
    [navigate, pushShare, setError, setPhase, manifest],
  );

  const handleCapture = useCallback(async () => {
    log.info('handleCapture', { phase, mode });
    if (phase !== 'idle' && phase !== 'done' && phase !== 'error') return;
    if (!engineRef.current) {
      setToast('Engine not ready');
      return;
    }
    setError(null);
    setPreview(null);

    try {
      if (mode === 'video') {
        await captureVideoFlow();
      } else {
        await capturePhotoFlow(SHOTS_BY_MODE[mode]);
      }
    } catch (err) {
      log.error('capture failed', err);
      setError(err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  }, [mode, phase, setError, setPhase]);

  const capturePhotoFlow = useCallback(
    async (shots: number) => {
      const frames: Blob[] = [];
      for (let i = 0; i < shots; i++) {
        if (shots > 1 || i === 0) {
          setPhase('countdown');
          await runCountdown({
            seconds: 3,
            onTick: (n) => setCountdown(n),
          });
        }
        setPhase('capturing');
        await runFlash();
        frames.push(await grabFrame());
        if (i < shots - 1) {
          await new Promise((r) => setTimeout(r, 800));
        }
      }
      setPhase('preview');
      let composed: Blob;
      if (shots === 1) {
        composed = frames[0]!;
        // For single-photo mode, the per-mode print frame is composited on
        // top by `compositeFrameOnto` if the manifest provides it.
        const single = manifest?.printFrames?.single?.url;
        if (single) {
          try {
            composed = await compositeFrameOnto(composed, single);
          } catch (err) {
            log.warn('single print-frame composite failed', err);
          }
        }
      } else {
        const stripMode = shots === 3 ? 'strip3' : 'strip4';
        const printFrameUrl =
          manifest?.printFrames?.[stripMode]?.url ?? undefined;
        composed = await composeStrip({
          frames,
          ...(printFrameUrl ? { printFrameUrl } : {}),
        });
      }
      const url = URL.createObjectURL(composed);
      setPreview({ url, kind: 'photo', blob: composed });
    },
    [grabFrame, runFlash, setCountdown, setPhase, manifest],
  );

  // Controller for the in-flight video recording — tapping the shutter
  // again (or programmatic abort) calls .abort() on this to stop early.
  const videoAbortRef = useRef<AbortController | null>(null);
  const stopRecording = useCallback(() => {
    const c = videoAbortRef.current;
    if (c && !c.signal.aborted) {
      log.info('stopRecording: user requested early stop');
      c.abort();
    }
  }, []);

  const captureVideoFlow = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const mime = negotiateVideoMime();
    if (!mime) {
      setToast('MediaRecorder not supported in this browser.');
      setPhase('error');
      return;
    }
    if (isIosSafari() && mime.ext === 'webm') {
      setToast('iOS Safari may not play this video — sharing should still work.');
    }
    setPhase('countdown');
    await runCountdown({ seconds: 3, onTick: (n) => setCountdown(n) });
    setPhase('capturing');
    setRecordingProgress(0);

    const audioTrack = camera.stream?.getAudioTracks()[0] ?? null;
    const controller = new AbortController();
    videoAbortRef.current = controller;

    try {
      const result = await recordVideo({
        canvas,
        audioTrack,
        maxSeconds: VIDEO_MAX_SECONDS,
        fps: 30,
        signal: controller.signal,
        onProgress: (elapsed) => {
          setRecordingProgress(
            Math.min(1, elapsed / (VIDEO_MAX_SECONDS * 1000)),
          );
        },
      });

      setRecordingProgress(0);
      setPhase('preview');
      const url = URL.createObjectURL(result.blob);
      setPreview({ url, kind: 'video', blob: result.blob });
    } finally {
      videoAbortRef.current = null;
    }
  }, [camera.stream, setCountdown, setPhase, setRecordingProgress]);

  const confirmUpload = useCallback(async () => {
    if (!preview) return;
    if (preview.kind === 'photo') {
      await handleUpload(preview.blob, 'photo', 'image/jpeg', 'jpg');
    } else {
      const mime = negotiateVideoMime();
      const ext = (mime?.ext ?? 'webm') as 'mp4' | 'webm';
      const contentType = mime?.mime.split(';')[0] ?? `video/${ext}`;
      await handleUpload(preview.blob, 'video', contentType, ext);
    }
  }, [preview, handleUpload]);

  const discardPreview = useCallback(() => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setPhase('idle');
  }, [preview, setPhase]);

  const downloadPreview = useCallback(() => {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = preview.kind === 'photo'
      ? `photobooth-${Date.now()}.jpg`
      : `photobooth-${Date.now()}.${negotiateVideoMime()?.ext ?? 'webm'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [preview]);

  if (engineUnsupported) {
    return <UnsupportedPage />;
  }

  // Choose the viewfinder frame whose aspect ratio best matches the current
  // canvas. Re-pick whenever the badge stats cache notices a dim change.
  const [frameTick, setFrameTick] = useState(0);
  useEffect(() => {
    if (!manifest?.viewfinderFrames.length) return;
    const id = window.setInterval(() => setFrameTick((n) => n + 1), 1500);
    return () => window.clearInterval(id);
  }, [manifest]);
  const activeFrame = useMemo(() => {
    if (!manifest?.viewfinderFrames.length) return null;
    const c = badgeStatsCache.current;
    return pickFrameForCanvas(
      manifest.viewfinderFrames,
      c.cw || window.innerWidth,
      c.ch || window.innerHeight,
    );
    // frameTick keeps this memo fresh when canvas dims change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, frameTick]);
  // Sync the URL into a ref so `grabFrame` (declared earlier) can read it
  // without a forward dependency.
  useEffect(() => {
    activeFrameUrlRef.current = activeFrame?.url ?? null;
  }, [activeFrame]);

  return (
    <div className={styles.layout}>
      <div className={styles.stage}>
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          aria-label="Live camera preview"
        />
        {activeFrame && (
          <img
            src={activeFrame.url}
            alt=""
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
              zIndex: 3,
            }}
          />
        )}
        <video
          ref={setVideoEl}
          className={styles.hiddenVideo}
          playsInline
          muted
          autoPlay
        />
        <CountdownOverlay value={countdownValue} />
        <Flash visible={flash} />
        {manifest && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(60px + var(--safe-top))',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '6px 14px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.1)',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text)',
              zIndex: 6,
              pointerEvents: 'none',
            }}
          >
            {manifest.title}
          </div>
        )}

        <div className={styles.topBar}>
          <div className={styles.topBarLeft}>
            {!manifest && <MakeYourOwnButton />}
            {statsVisible && (
              <div className={styles.badge}>
                {engineBackend ? engineBackend.toUpperCase() : 'GPU…'}
                <span ref={badgeStatsRef} />
                {faceTrackingAvailable === true && ' · Face'}
                {faceTrackingAvailable === false && ' · No face'}
                {rotationOverride !== null && ` · rot${rotationOverride * 90}°`}
              </div>
            )}
          </div>
          <div className={styles.topBarRight}>
            <button
              type="button"
              className={styles.rotateButton}
              onClick={cycleRotation}
              aria-label="Rotate camera view"
              title="Cycle rotation 0° → 90° → 180° → 270° → Auto"
            >
              ↻
            </button>
            {(isMobile() || camera.devices.length > 1) && (
              <CameraFlipButton
                disabled={phase === 'capturing' || phase === 'countdown' || phase === 'uploading'}
                onClick={() => void camera.flip()}
              />
            )}
            <FooterCredit />
          </div>
        </div>

        {camera.status.kind === 'denied' && (
          <CameraDeniedOverlay onRetry={() => void camera.retry()} />
        )}
        {camera.status.kind === 'error' && (
          <CameraErrorOverlay message={camera.status.message} onRetry={() => void camera.retry()} />
        )}
        {camera.status.kind === 'requesting' && <CameraRequestingOverlay />}
      </div>

      <div className={styles.controls}>
        <div className={styles.modeRow}>
          <ModePicker
            mode={mode}
            disabled={phase === 'capturing' || phase === 'countdown' || phase === 'uploading'}
            onChange={setMode}
            allowed={manifest?.modes}
          />
        </div>
        <div className={styles.captureRow}>
          <EffectDrawer
            current={effect}
            faceAvailable={faceTrackingAvailable !== false}
            onSelect={setEffect}
            allowedEffects={manifest?.effects as EffectIdName[] | undefined}
            onOpen={() => {
              // Pre-load MediaPipe so a face-tracked effect is responsive.
              if (!faceTrackingNeeded) {
                log.info('drawer opened — prefetching face tracker');
                setFaceTrackingNeeded(true);
              }
            }}
          />
          <ShutterButton
            recording={mode === 'video' && phase === 'capturing'}
            progress={recordingProgress}
            disabled={
              camera.status.kind !== 'ready' ||
              phase === 'countdown' ||
              phase === 'uploading' ||
              // Photo modes lock during 'capturing'; video stays tappable
              // so the user can stop early.
              (phase === 'capturing' && mode !== 'video')
            }
            onClick={() => {
              if (mode === 'video' && phase === 'capturing') {
                stopRecording();
                return;
              }
              void handleCapture();
            }}
            label={
              mode === 'video'
                ? phase === 'capturing'
                  ? 'Stop recording'
                  : 'Record up to 30s'
                : 'Capture'
            }
          />
        </div>
      </div>

      {preview && (
        <div className={styles.preview} role="dialog" aria-modal>
          {preview.kind === 'photo' ? (
            <img src={preview.url} alt="captured" className={styles.previewMedia} />
          ) : (
            <video
              src={preview.url}
              controls
              autoPlay
              playsInline
              className={styles.previewMedia}
            />
          )}
          {phase === 'uploading' ? (
            <p>Uploading…</p>
          ) : (
            <div className={styles.row}>
              <Button variant="ghost" onClick={discardPreview}>
                Retake
              </Button>
              <Button variant="secondary" onClick={downloadPreview}>
                Download
              </Button>
              <Button variant="primary" onClick={() => void confirmUpload()}>
                Share
              </Button>
            </div>
          )}
          {errorMessage && (
            <p style={{ color: 'var(--danger)', maxWidth: 360, textAlign: 'center' }}>
              {errorMessage}
            </p>
          )}
        </div>
      )}

      {toast && <Toast message={toast} />}
    </div>
  );
}

function CameraDeniedOverlay({ onRetry }: { onRetry(): void }) {
  return (
    <div
      role="alert"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        textAlign: 'center',
        zIndex: 12,
      }}
    >
      <h2>Camera access blocked</h2>
      <p style={{ color: 'var(--text-dim)', maxWidth: 360 }}>
        Allow camera in your browser settings — usually the address-bar lock
        icon — then tap retry.
      </p>
      <Button variant="primary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function CameraErrorOverlay({ message, onRetry }: { message: string; onRetry(): void }) {
  return (
    <div
      role="alert"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        textAlign: 'center',
        zIndex: 12,
      }}
    >
      <h2>Camera error</h2>
      <p style={{ color: 'var(--text-dim)', maxWidth: 360 }}>{message}</p>
      <Button variant="primary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function CameraRequestingOverlay() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 11,
      }}
    >
      <p>Asking for camera permission…</p>
    </div>
  );
}
